import React, { useState } from 'react';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MenuIcon from '@mui/icons-material/Menu';
import PersonIcon from '@mui/icons-material/Person';
import TransferWithinAStationIcon from '@mui/icons-material/TransferWithinAStation';
import {
  Avatar,
  Toolbar,
  Typography,
  IconButton,
  Tooltip,
  Menu,
  MenuItem,
  Button,
  Chip,
  Box,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { isLoginAsUser } from '../auth/isAuth';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { tokens } from '../../shared/theme/tokens';
import type { User } from '../types';
import AppBreadcrumbs from './AppBreadcrumbs';

interface ToolBarProps {
  handleRestoreUser: () => void;
  phoneResponsive: boolean;
  darkState: boolean;
  handleThemeChange: () => void;
  handleDisconnect: () => void;
  user: User | null;
  onMenuClick: () => void;
}

interface SimpleMenuProps {
  handleRestoreUser: () => void;
  phoneResponsive: boolean;
  anchorEl: HTMLElement | null;
  handleClose: () => void;
  handleThemeChange: () => void;
  darkState: boolean;
  handleDisconnect: () => void;
  handleChangeProfil: () => void;
  user: User | null;
}

export default function ToolBar({
  handleRestoreUser,
  phoneResponsive,
  darkState,
  handleThemeChange,
  handleDisconnect,
  user,
  onMenuClick,
}: ToolBarProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const impersonating = isLoginAsUser();

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleChangeProfil = () => {
    if (!user?.uuid) return;
    navigate('/');
    navigate(`/profil/${user.uuid}`);
  };

  const showMenuButton = isMobile;

  return (
    <Toolbar
      disableGutters
      sx={{
        width: '100%',
        minHeight: { xs: 56, sm: 64 },
        px: { xs: 1, sm: 2 },
        gap: 1,
        ...(impersonating && {
          bgcolor: tokens.colors.impersonation,
          borderRadius: `${tokens.shape.borderRadius}px`,
        }),
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
        {showMenuButton && (
          <IconButton
            edge="start"
            color="inherit"
            aria-label="Ouvrir le menu"
            onClick={onMenuClick}
            sx={{ mr: 0.5, flexShrink: 0 }}
          >
            <MenuIcon />
          </IconButton>
        )}

        {impersonating && (
          <Chip
            size="small"
            label="Mode administrateur"
            color="primary"
            variant={darkState ? 'filled' : 'outlined'}
            onDelete={!phoneResponsive ? handleRestoreUser : undefined}
            deleteIcon={
              !phoneResponsive ? (
                <Tooltip title="Revenir à mon compte admin">
                  <TransferWithinAStationIcon fontSize="small" />
                </Tooltip>
              ) : undefined
            }
            sx={{ display: { xs: 'none', md: 'flex' }, maxWidth: 220, flexShrink: 0 }}
          />
        )}

        {!phoneResponsive && impersonating && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={handleRestoreUser}
            sx={{ display: { xs: 'none', md: 'inline-flex' }, flexShrink: 0 }}
          >
            Reconnexion admin
          </Button>
        )}

        <AppBreadcrumbs />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          ml: 'auto',
          mr: { xs: 0.25, sm: 0.5 },
          flexShrink: 0,
          cursor: 'pointer',
          borderRadius: 2,
          px: 1,
          py: 0.5,
          transition: 'background-color 0.2s ease',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={handleClick}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') handleClick(e as unknown as React.MouseEvent<HTMLElement>);
        }}
      >
        {!phoneResponsive && user && (
          <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
            <Typography variant="body2" fontWeight={600} lineHeight={1.2}>
              {user.firstName}
            </Typography>
            <Typography variant="caption" color="text.secondary" lineHeight={1.2}>
              {user.lastName}
            </Typography>
          </Box>
        )}
        <Avatar
          alt={user ? `${user.firstName} ${user.lastName}` : 'Menu utilisateur'}
          src={user?.photo?.contentUrl}
          sx={{
            width: 40,
            height: 40,
            bgcolor: darkState ? 'secondary.main' : 'primary.main',
            color: darkState ? 'primary.dark' : 'primary.contrastText',
            fontWeight: 700,
          }}
        >
          {user?.firstName?.charAt(0) ?? '?'}
        </Avatar>
      </Box>

      <SimpleMenu
        handleRestoreUser={handleRestoreUser}
        phoneResponsive={phoneResponsive}
        user={user}
        anchorEl={anchorEl}
        handleClose={handleClose}
        handleChangeProfil={handleChangeProfil}
        handleDisconnect={handleDisconnect}
        handleThemeChange={handleThemeChange}
        darkState={darkState}
      />
    </Toolbar>
  );
}

function SimpleMenu({
  handleRestoreUser,
  phoneResponsive,
  anchorEl,
  handleClose,
  handleThemeChange,
  darkState,
  handleDisconnect,
  handleChangeProfil,
  user,
}: SimpleMenuProps) {
  return (
    <Menu
      id="user-menu"
      anchorEl={anchorEl}
      open={Boolean(anchorEl)}
      onClose={handleClose}
      onClick={handleClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      PaperProps={{
        elevation: 3,
        sx: { minWidth: 220, mt: 1, borderRadius: 2 },
      }}
    >
      {phoneResponsive && user && (
        <>
          <Box sx={{ px: 2, py: 1.5 }}>
            <Typography variant="subtitle2" fontWeight={600}>
              {user.firstName} {user.lastName}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {user.email}
            </Typography>
          </Box>
          <Divider />
        </>
      )}
      <MenuItem onClick={handleThemeChange}>
        <ListItemIcon>
          {darkState ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
        </ListItemIcon>
        <ListItemText>{darkState ? 'Thème clair' : 'Thème sombre'}</ListItemText>
      </MenuItem>
      <MenuItem onClick={handleChangeProfil}>
        <ListItemIcon>
          <PersonIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Mon profil</ListItemText>
      </MenuItem>
      {isLoginAsUser() && phoneResponsive && (
        <MenuItem onClick={handleRestoreUser}>
          <ListItemIcon>
            <TransferWithinAStationIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Reconnexion admin</ListItemText>
        </MenuItem>
      )}
      <Divider />
      <MenuItem onClick={handleDisconnect}>
        <ListItemIcon>
          <ExitToAppIcon fontSize="small" color="error" />
        </ListItemIcon>
        <ListItemText sx={{ color: 'error.main' }}>Se déconnecter</ListItemText>
      </MenuItem>
    </Menu>
  );
}
